import { Combinator, Operator } from '@procureai/query-builder';
import { RuleGroupInternalType, toObject } from '@procureai/query-builder';

import { QueryCombinator, QueryOperator, SavingQueryFilterInput } from '@/generated/gql/graphql';
import { type SavingQueryInput } from '@/generated/gql/graphql';

// The grid sorts and filters client-side, so a sort is only correct over the
// rows that were actually loaded. The unfiltered initial view stays small; a
// filtered view (typically one buyer, a validity range) loads the full result
// so that e.g. "sort by % saving" is not computed over an arbitrary first page.
export const SAVINGS_QUERY_LIMIT = 100;
export const SAVINGS_FILTERED_QUERY_LIMIT = 1000;

const queryCombinatorMap = {
  [Combinator.AND]: QueryCombinator.AND,
  [Combinator.OR]: QueryCombinator.OR,
} as const satisfies Record<Combinator, QueryCombinator>;

type SupportedQueryBuilderOperator =
  | Operator.EQ
  | Operator.GT
  | Operator.GTE
  | Operator.LT
  | Operator.LTE
  | Operator.NE
  | Operator.IN
  | Operator.NIN
  | Operator.INEQ
  | Operator.NINEQ
  | Operator.BETWEEN
  | Operator.NOT_BETWEEN
  | Operator.EMPTY
  | Operator.NOT_EMPTY;

const queryOperatorMap = {
  [Operator.EQ]: QueryOperator.EQUALS,
  [Operator.GT]: QueryOperator.GREATER_THAN,
  [Operator.GTE]: QueryOperator.GREATER_THAN_OR_EQUAL,
  [Operator.LT]: QueryOperator.LESS_THAN,
  [Operator.LTE]: QueryOperator.LESS_THAN_OR_EQUAL,
  [Operator.NE]: QueryOperator.NOT_EQUALS,
  [Operator.IN]: QueryOperator.IN,
  [Operator.NIN]: QueryOperator.NOT_IN,
  [Operator.INEQ]: QueryOperator.IN_EQUALS,
  [Operator.NINEQ]: QueryOperator.NOT_IN_EQUALS,
  [Operator.BETWEEN]: QueryOperator.BETWEEN,
  [Operator.NOT_BETWEEN]: QueryOperator.NOT_BETWEEN,
  [Operator.EMPTY]: QueryOperator.IS_EMPTY,
  [Operator.NOT_EMPTY]: QueryOperator.IS_NOT_EMPTY,
} as const satisfies Record<SupportedQueryBuilderOperator, QueryOperator>;

interface QueryBuilderRule {
  field: string;
  operator: string;
  value?: unknown;
}

export interface QueryBuilderGroup {
  combinator?: string;
  not?: boolean;
  rules: QueryBuilderRule[];
}

const toQueryCombinator = (combinator?: string): QueryCombinator | undefined => {
  if (!combinator) return undefined;

  const normalizedCombinator = queryCombinatorMap[combinator as Combinator];

  if (!normalizedCombinator) {
    throw new Error(`Unsupported savings query combinator: ${combinator}`);
  }

  return normalizedCombinator;
};

const toQueryOperator = (operator: string): QueryOperator => {
  const normalizedOperator = queryOperatorMap[operator as SupportedQueryBuilderOperator];

  if (!normalizedOperator) {
    throw new Error(`Unsupported savings query operator: ${operator}`);
  }

  return normalizedOperator;
};

export const normalizeQueryBuilderFilter = (group: QueryBuilderGroup): SavingQueryFilterInput => ({
  combinator: toQueryCombinator(group.combinator),
  not: group.not,
  rules: group.rules.map((rule) => ({
    field: rule.field,
    operator: toQueryOperator(rule.operator),
    value: rule.value as SavingQueryFilterInput['rules'][number]['value'],
  })),
});

export const getSavingsQueryVariable = (query: RuleGroupInternalType | undefined): SavingQueryInput => {
  // Always send the limit — without a query object the backend falls back to
  // its own default of 50 rows.
  if (!query) return { limit: SAVINGS_QUERY_LIMIT };

  const { count, group } = toObject(query);

  if (!count) return { limit: SAVINGS_QUERY_LIMIT };

  return {
    filter: normalizeQueryBuilderFilter(group as QueryBuilderGroup),
    limit: SAVINGS_FILTERED_QUERY_LIMIT,
  };
};
